import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { OutboxUtils } from "Utils/OutboxUtils";
import { ReactComponent as LinkedinIcon } from "Assets/svg/linkedInOutlined.svg";
import { ReactComponent as SequenceIcon } from "Assets/svg/sequence.svg";
import inboxV2Store from "./inboxV2Store";
import { formatDay, formatTime, getStepBySequence } from "./inboxV2Utils";
import { SeqCardSkeleton, ConvRowSkeleton } from "./InboxV2Loader";
import Avatar, { getInitials, getContactName } from "./Avatar";

// Each sequence opens its builder in a new tab.
const openSequence = seq => {
  const url = OutboxUtils.getSequenceURL({
    id: seq?.sequenceId,
    folderId: seq?.folderId,
    shareType: seq?.shareType
  });
  if (url) window.open(url, "_blank", "noopener");
};

const PAUSED_STATUSES = ["PAUSED", "PAUSED_OOTO"];

const isPaused = (status = "") =>
  PAUSED_STATUSES.includes((status || "").toUpperCase());

const CONV_PAGE_SIZE = 10;

const ConversationsPane = observer(() => {
  const [activeTab, setActiveTab] = useState("conversations");
  // Client-side "infinite scroll": /conversations returns the whole thread set for the
  // contact, so we reveal CONV_PAGE_SIZE more each time the list nears its bottom.
  const [visibleCount, setVisibleCount] = useState(CONV_PAGE_SIZE);
  const {
    selectedContact = null,
    feed = [],
    openConversationId = "",
    taggedSequences = [],
    conversationsLoading = false,
    sequencesLoading = false
  } = inboxV2Store || {};

  const { firstName = "", lastName = "", email = "" } =
    selectedContact?.contact?.[0] || {};

  const stepBySequence = getStepBySequence(feed);
  const seqBySequence = taggedSequences.reduce((acc, seq) => {
    if (seq?.sequenceId) acc[seq.sequenceId] = seq;
    return acc;
  }, {});
  const pausedCount = taggedSequences.filter(seq =>
    isPaused(seq?.contactStatusInSequence)
  ).length;
  const allPaused =
    taggedSequences.length > 0 && pausedCount === taggedSequences.length;

  const contactId = inboxV2Store.getContactId(selectedContact || {});
  useEffect(() => {
    setVisibleCount(CONV_PAGE_SIZE);
    setActiveTab("conversations");
  }, [contactId]);

  const handleScroll = event => {
    if (activeTab !== "conversations") return;
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - (scrollTop + clientHeight) < 60 &&
      visibleCount < feed.length
    ) {
      setVisibleCount(count => count + CONV_PAGE_SIZE);
    }
  };

  const visibleFeed = feed.slice(0, visibleCount);

  const tabs = [
    { id: "conversations", label: "Conversations", count: feed.length },
    { id: "sequences", label: "Sequences", count: taggedSequences.length }
  ];

  return (
    <div className="inboxV2ConversationsPane">
      <div className="inboxV2PaneHeader">
        <div className="inboxV2PaneContact">
          <Avatar initials={getInitials(firstName, lastName, email)} />
          <div className="inboxV2PaneContactMeta">
            <div className="inboxV2PaneName">
              {getContactName(firstName, lastName, email)}
            </div>
            <div className="inboxV2PaneEmail" title={email}>
              {email}
            </div>
          </div>
        </div>

        {allPaused && (
          <div className="inboxV2Banner">
            All sequences are paused for this contact
          </div>
        )}

        <div className="inboxV2PaneTabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`inboxV2PaneTab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="inboxV2TabCount">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="inboxV2TabBody" onScroll={handleScroll}>
        {activeTab === "conversations" ? (
          conversationsLoading ? (
            Array.from({ length: 6 }, (_, index) => (
              <ConvRowSkeleton key={`convSkeleton${index}`} />
            ))
          ) : feed.length > 0 ? (
            visibleFeed.map(item => {
              const isLinkedIn = item?.type === "linkedin";
              const isOpen = item?.id === openConversationId;
              const isUnread = !!item?.unread;
              const last = item?.messages?.[item.messages.length - 1] || {};
              return (
                <div
                  className={`inboxV2ConvRow ${isOpen ? "active" : ""} ${
                    isUnread ? "unread" : ""
                  }`}
                  key={item?.id}
                  onClick={() => inboxV2Store.openConversation(item)}
                >
                  <span
                    className={`inboxV2ChannelIcon ${
                      isLinkedIn ? "linkedin" : "email"
                    }`}
                  >
                    {isLinkedIn ? (
                      <LinkedinIcon />
                    ) : (
                      // Closed envelope for unread, open envelope for read.
                      <i className="material-icons">
                        {isUnread ? "mail" : "drafts"}
                      </i>
                    )}
                  </span>
                  <div className="inboxV2ConvMeta">
                    <div className="inboxV2ConvTop">
                      <span className="inboxV2ConvFrom">
                        {last?.dir === "in" ? last?.from || "Contact" : "You"}
                      </span>
                      {item?.externalGmail && (
                        <span className="inboxV2GmailChip">Gmail</span>
                      )}
                    </div>
                    <div className="inboxV2ConvPreview">
                      {!isLinkedIn && item?.subject && (
                        <span className="inboxV2ConvSubject">
                          {item.subject}{" "}
                        </span>
                      )}
                      <span>{item?.preview}</span>
                    </div>
                    {item?.sequenceId && seqBySequence[item.sequenceId] && (
                      <button
                        type="button"
                        className="inboxV2ConvSeq"
                        title={seqBySequence[item.sequenceId]?.name}
                        onClick={event => {
                          event.stopPropagation();
                          openSequence(seqBySequence[item.sequenceId]);
                        }}
                      >
                        <SequenceIcon />
                        <span>{seqBySequence[item.sequenceId]?.name}</span>
                      </button>
                    )}
                    <div className="inboxV2ConvDate">
                      {formatDay(item?.latestDate)}
                      {item?.latestDate
                        ? ` · ${formatTime(item.latestDate)}`
                        : ""}
                      {item?.messages?.length > 1
                        ? ` · ${item.messages.length} messages`
                        : ""}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="inboxV2Muted">No conversations found</div>
          )
        ) : (
          <div className="inboxV2SeqList">
            {sequencesLoading ? (
              Array.from({ length: 2 }, (_, index) => (
                <SeqCardSkeleton key={`seqSkeleton${index}`} />
              ))
            ) : taggedSequences.length > 0 ? (
              taggedSequences.map((seq, index) => {
                const {
                  sequenceId = "",
                  name: seqName = "",
                  contactStatusInSequence = "",
                  scheduledSeqId = ""
                } = seq || {};
                const paused = isPaused(contactStatusInSequence);
                const step = stepBySequence[sequenceId] || "";
                return (
                  <button
                    type="button"
                    className={`inboxV2SeqCard ${paused ? "paused" : ""}`}
                    key={scheduledSeqId || `seq${index}`}
                    title="Open sequence"
                    onClick={() => openSequence(seq)}
                  >
                    <div className="inboxV2SeqRow">
                      <div className="inboxV2SeqInfo">
                        <div className="inboxV2SeqName">{seqName}</div>
                        {step && <div className="inboxV2SeqStep">{step}</div>}
                      </div>
                      <span
                        className={`inboxV2SeqStatus ${
                          paused ? "paused" : "active"
                        }`}
                      >
                        {paused ? "Paused" : "Active"}
                      </span>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="inboxV2Muted">No sequences</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default ConversationsPane;
